import { tzFormat, onDisplayTzChange } from './appClock'
// ============================================

// -- Time label formatting (draw hot path) ------------------------------
// These labels were produced by constructing a locale formatter INLINE, once
// per label group, on EVERY draw. Those Date locale methods are constructor
// calls in disguise: each negotiates the locale, loads ICU data, resolves the
// options into a skeleton, compiles a pattern and allocates a formatter — then
// formats one value and discards all of it. Setup dominates by ~100x; the
// formatting itself is nanoseconds. Measured here: 200 labels = 57ms inline
// vs 0ms with a hoisted formatter. (The same mistake in MessagesPanel was
// costing ~50% of the main thread.)
//
// Two formats are needed because label granularity depends on timeInterval,
// so both are built ONCE and reused. `[]` (engine default locale) is
// preserved exactly as before — no output change.
const FMT_DAY = { format: (d) => tzFormat({ month: 'short', day: 'numeric' }).format(d) }
const FMT_HM  = { format: (d) => tzFormat({ hour: '2-digit', minute: '2-digit', hour12: false }).format(d) }

// Memoised by (format, timestamp): a candle's startTime never changes, so its
// label never changes. After the first draw every group header is a Map
// lookup. Bounded — scrolling deep history would otherwise accumulate one
// entry per candle ever seen.
const labelCache = new Map()
onDisplayTzChange(() => labelCache.clear())   // display zone changed -> relabel
const fmtTimeLabel = (fmt, key, ts) => {
  const k = key + ts
  let v = labelCache.get(k)
  if (v === undefined) {
    v = fmt.format(new Date(ts))
    if (labelCache.size > 2000) labelCache.clear()   // simple bound; refills lazily
    labelCache.set(k, v)
  }
  return v
}

// Header time label for one candle start. Hours and minutes below 1d (no
// seconds, sub-minute TFs included); month + day on 1d. ONE formatter for
// the header cells AND the crosshair's header chip, so they always agree.
export function headerTimeLabel(ts, timeInterval) {
  return timeInterval >= 86400
    ? fmtTimeLabel(FMT_DAY, 'd', ts)
    : fmtTimeLabel(FMT_HM,  'm', ts)
}

// chartGeometry.js — pure mcap↔row/pixel geometry + header/footer cell
// grouping shared by FootprintChartCanvas. Everything here is a plain
// function of its arguments: no hooks, no refs, no DOM.
// ============================================
import { ceilBucket, latticeRowIndex, logStepIdx, logStepEdge } from './chartUtils'
import { candleDelta } from './candleHelpers'

// ── Shared mcap range scan ──
// ONE implementation for both consumers: the visibleSortedMcaps grid memo
// and computeIdealBucket (via scanVisibleMcapRange). They previously
// carried copy-pasted loops with a "must match exactly" comment — and had
// already drifted (the sizer was missing the highlight stretch below).
// Historical candles: raw high/low, NO bucket-snap (snapping made dataRange
// depend on bucketSize and fed a rebucket feedback loop at high yZoom).
// Live candles: read _liveLevels/_native at the constant _nativeBucket;
// deliberately skip forEachLevel — its first branch reads _display, which
// is displayBucket-aligned and would re-introduce that coupling.
// Returns { min, max }; either may be ±Infinity — callers keep their own
// validity policy.
export function scanCandlesMcapRange(visibleCandles) {
  let min = Infinity, max = -Infinity
  for (const candle of visibleCandles) {
    if (candle.isHistorical) {
      if (candle.high_mcap != null) {
        if (candle.high_mcap > max) max = candle.high_mcap
        if (candle.low_mcap  < min) min = candle.low_mcap
      }
    } else {
      const nb = candle._nativeBucket
      if (candle._liveLevels && candle._liveLevels.size > 0 && nb) {
        let minStep = Infinity, maxStep = -Infinity
        for (const k of candle._liveLevels.keys()) {
          if (k < minStep) minStep = k
          if (k > maxStep) maxStep = k
        }
        const lo = minStep * nb
        const hi = (maxStep + 1) * nb
        if (hi > max) max = hi
        if (lo < min) min = lo
      } else if (candle._native && candle._native.length >= 3 && nb) {
        const a = candle._native
        const lo = a[0] * nb
        const hi = (a[a.length - 3] + 1) * nb
        if (hi > max) max = hi
        if (lo < min) min = lo
      }
    }
  }
  return { min, max }
}

// ── Highlight stretch (autoscroll only) ──
// Extends the scanned range to include the live price when it has pushed
// genuinely past the recorded data. Shared by the grid memo AND the bucket
// sizer so both measure the SAME dataRange — previously the sizer omitted
// this, undershot idealBucket exactly when price led the prints, and
// rebuckets lagged/oscillated mid-trend.
// Mechanics preserved verbatim from the memo:
// - margin quantized to WHOLE buckets — a continuous margin let an
//   edge-of-bucket swap slide dataCenter/desiredTop fractionally across a
//   snap boundary (back-and-forth jitter at the lows)
// - extremes pushed to the LATTICE position of the highlight bucket, so
//   they change ONLY on a real bucket crossing, in lockstep with the grid
// - halfBucket tolerance: minMcap is the BOTTOM of the lowest recorded
//   step's range, so a strict < stretched on every wiggle inside the
//   bottom step (ghost shifts at the boundary on every visit); the
//   stretch fires only when highlight pushes past by more than half a
//   bucket. Symmetric on the high side.
export function stretchRangeForHighlight(min, max, highlightMcap, bucketSize, ratio = null) {
  // Stretch lands EXACTLY on the highlight's own lattice cell — no margin.
  // The old version added max(1 bucket, ~2% of range) beyond price, which
  // produced the "incoherent lows": the viewport expanded PAST price
  // before it got there (anticipatory empty rows), and because the margin
  // was render-time-only — persisted nowhere — it vanished the moment the
  // highlight ticked back inside the printed range. Printed lows commit
  // via the candle scan; the margin could never commit by construction,
  // so every boundary approach was expand-overshoot → snap-back. Breathing
  // room is computeRangeBuffer's job downstream (applied uniformly to
  // committed AND stretched ranges); padding here was double-padding with
  // the inner layer unstable. The half-bucket tolerance stays: it stops
  // edge-of-printed-range tick noise from toggling a row in and out.
  // LOG: the same shape with multiplication where the linear path adds. A
  // "half bucket" of tolerance becomes sqrt(ratio) — half a step in ln-space —
  // and the lattice snap is logStepEdge/logStepIdx, the SAME global anchored-at-1
  // lattice the grid memo and buildDisplay use. Using a different anchor here
  // would put the stretched extreme between rows.
  if (ratio && highlightMcap > 0) {
    const half = Math.sqrt(ratio)
    if (highlightMcap > max * half) {
      max = logStepEdge(logStepIdx(highlightMcap, ratio) + 1, ratio)
    }
    if (highlightMcap < min / half) {
      min = logStepEdge(logStepIdx(highlightMcap, ratio), ratio)
    }
    return { min, max }
  }
  const halfBucket = bucketSize / 2
  if (highlightMcap > max + halfBucket) {
    max = ceilBucket(highlightMcap, bucketSize)
  }
  if (highlightMcap < min - halfBucket) {
    min = Math.floor(highlightMcap / bucketSize) * bucketSize
  }
  return { min, max }
}

// ── Price → grid row: bucket-floor MEMBERSHIP ──
// THE one mapping from a live price to a grid row, used by the canvas
// highlight band (priceToRow), the y-axis current-row class, and the
// current-price overlay chip. Trades print into floor(mcap/bucket) cells,
// so the highlight machinery must use the same mapping — Math.round
// (geometric nearest) drifted one row high whenever price sat in the
// upper half of its bucket. Integer-step math keeps fractional buckets
// exact; floor always yields a row (no tolerance-miss flicker) and
// callers bounds-check.
export function bucketRowIndex(mcaps, price) {
  // LOG-AWARE via latticeRowIndex, which reads the mode off the array
  // (mcaps.log). The two lattices differ only in whether a row is a constant
  // difference or a constant ratio; this function's contract — UNCLAMPED, so
  // out-of-lattice prices keep their direction — is unchanged, and that is
  // why it does not simply call findBucketIndex (see the note below).
  // ONE CALL. This used to delegate to latticeRowIndex for log and then
  // re-implement the LINEAR branch inline — a copy of
  // `ceil((top - value) / bucket - 1e-9)` carrying a comment saying the two
  // "must stay formula-identical". A duplicate with a note asking you to
  // remember is a drift waiting to happen; latticeRowIndex already handles
  // both modes off mcaps.log and is unclamped, which is exactly this
  // function's contract.
  //
  // The clamp is what still separates the two CALLERS, and that distinction
  // is real — see the note below. findBucketIndex is the clamped wrapper
  // (markers: out-of-lattice means nothing to draw). This is the raw one
  // (geometry: an above-grid high must stay negative so its wick clips off
  // the top instead of snapping to y=0).
  return latticeRowIndex(mcaps, price)
  // The semantics differ from findBucketIndex on purpose:
  //   findBucketIndex → MARKERS (volume bars, alert/limit/position tags):
  //     out-of-lattice means "nothing to draw", -1 is correct.
  //   bucketRowIndex → GEOMETRY (wick/body endpoints, chip, band):
  //     out-of-lattice prices must keep their DIRECTION — a high above
  //     the grid maps to a negative row (drawn off-canvas, clipped), a
  //     low below maps past length (clipped). A brief delegation to
  //     findBucketIndex collapsed both directions to -1, which snapped a
  //     below-grid low's wick endpoint to y=0 — the canvas TOP — tearing
  //     wicks off their bodies whenever an extreme exited the lattice.
  //     Chip/band callers bounds-check, so unclamped is safe everywhere.
}

// ── Grouped header/footer cells ──
// Merge candles into label/delta groups when pxPerCandle is too narrow for
// text. Pure extraction of the groupedCells memo body — the memo in the
// component just calls this.
//
// SAME RANGE AS THE FOOTPRINT. The column loop paints startIdx-1 .. endIdx+1
// (_loopLo/_loopHi) because the sub-candle pan offset slides those columns
// part-way into view. This used to build only startIdx..endIdx, so an edge
// label vanished the moment its candle touched the edge. Now it covers the
// same ±1, widened to whole groups on an ABSOLUTE grid (index multiples of
// groupSize), so a group keeps its candles and its label while it pans
// instead of re-slicing every step. Extra width falls off the clipped
// viewport. rightPadPx = how far the last cell runs past endIdx's right edge;
// the component uses it with frame.xLeft(endIdx) to place the row.
export function buildGroupedCells({ candles, startIdx, endIdx, pxPerCandle, timeInterval }) {
  const px = pxPerCandle
  const total = candles ? candles.length : 0
  if (!(px > 0) || !(endIdx >= 0) || !(endIdx >= startIdx)) {
    return { groups: [], groupSize: 1, endIdx, rightPadPx: 0 }
  }

  // Min width to fit a time label (~55px) or delta (~40px); use the wider constraint
  const minGroupWidth = 55
  const groupSize = Math.max(1, Math.ceil(minGroupWidth / px))
  const groups = []

  const lo = Math.max(0, startIdx - 1)
  const hi = endIdx + 1
  const first = Math.floor(lo / groupSize) * groupSize
  const last = Math.floor(hi / groupSize) * groupSize + groupSize - 1

  for (let g0 = first; g0 <= last; g0 += groupSize) {
    // Slots past the last candle (future padding) are empty ghost cells.
    const candle = g0 < total ? candles[g0] : null
    let label = ''
    let delta = 0
    if (candle) {
      // Header: group's first candle. Hours and minutes only below 1d,
      // sub-minute TFs included (no seconds).
      label = headerTimeLabel(candle.startTime, timeInterval)
      // Footer: sum deltas across group
      const gEnd = Math.min(g0 + groupSize, total)
      for (let i = g0; i < gEnd; i++) delta += candleDelta(candles[i])
    }
    groups.push({ label, delta, widthPx: groupSize * px, cellCount: groupSize })
  }

  return { groups, groupSize, endIdx, rightPadPx: (last - endIdx) * px }
}